import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import React from 'react'

import {
  FULFILMENT_META,
  money
} from 'app/pages/Apartment/Procurement/constants'

// The daily sheet used to print one button per waiting order across the top of
// the page. Every order for the same apartment read identically ("Hand over:
// At Apartment HQ · 52 lines" eight times over), so there was no telling them
// apart and no way to see what was in one before moving it (2026-10-09).
// This is the list behind the single button that replaced them: one row per
// order with enough to recognise it, its items a click away, and the action on
// the row. The action still goes through the daily sheet's own confirm.
//
// Items can be taken off an order here before it moves, when the caller passes
// onRemoveLine — not everything approved has to be bought. The At Apartment
// requests grid uses the same list for its open drafts (kind 'submit').

const KIND = {
  review: {
    title: 'Orders to review',
    intro:
      'Approved by the At Apartment head. Check the items, then add the order ' +
      'to the daily sheet.',
    action: 'Add to sheet',
    dateLabel: 'Approved',
    dateOf: (r) => r.final_approved_at
  },
  submit: {
    title: 'Open drafts',
    intro:
      'Not yet sent for approval. Check the items, remove any you do not ' +
      'want, then submit the request.',
    action: 'Submit',
    dateLabel: 'Created',
    dateOf: (r) => r.created
  },
  hand_over: {
    title: 'Orders to hand over',
    intro:
      'On the daily sheet and not yet handed to the At Apartment team. Check ' +
      'the items, then hand the order over.',
    action: 'Hand over',
    dateLabel: 'On sheet',
    dateOf: (r, lines) => sheetDate(lines)
  }
}

const shortDate = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

// The earliest sheet date on the order's lines — the day it went on the sheet.
const sheetDate = (lines) =>
  lines
    .map((l) => l.sheet_date)
    .filter(Boolean)
    .sort()[0]

// A draft line has no invoice yet, so fall back to its taxable total.
const lineAmount = (l) => Number(l.invoice_amount) || Number(l.line_total) || 0

const orderTotal = (lines) => lines.reduce((sum, l) => sum + lineAmount(l), 0)

const deliveredCount = (lines) =>
  lines.filter((l) => l.fulfilment_status === 'delivered').length

// Frozen while the list scrolls (2026-10-09): the column headings, then the
// order being read (its PO, total and action button), then its item headings —
// a 53-line order otherwise scrolls its own "Add to sheet" button off the top.
// Each layer sits just below the one above, so the heights are measured.
const frozen = (top, zIndex, background) => ({
  position: 'sticky',
  top,
  zIndex,
  background
})

const useHeight = () => {
  const ref = React.useRef(null)
  const [height, setHeight] = React.useState(0)
  React.useLayoutEffect(() => {
    const h = ref.current ? ref.current.offsetHeight : 0
    if (h !== height) setHeight(h)
  })
  return [ref, height]
}

// One item of an order. Removing asks on the row itself — "Remove? Yes / No" —
// so a slip of the mouse on a long list does not drop a line.
const ItemRow = ({ line, index, kind, busy, onRemove }) => {
  const [asking, setAsking] = React.useState(false)
  return (
    <TableRow>
      <TableCell>{index + 1}</TableCell>
      <TableCell>{line.item_name}</TableCell>
      <TableCell align="right">
        {line.qty} {line.unit || ''}
      </TableCell>
      <TableCell>{line.vendor || '—'}</TableCell>
      {kind === 'hand_over' && (
        <TableCell>
          {(FULFILMENT_META[line.fulfilment_status] || {}).label || '—'}
        </TableCell>
      )}
      <TableCell align="right">
        {lineAmount(line) ? money(lineAmount(line)) : '—'}
      </TableCell>
      {onRemove && (
        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
          {asking ? (
            <>
              <Typography variant="caption">Remove? </Typography>
              <Button
                size="small"
                color="primary"
                disabled={busy}
                onClick={() => {
                  setAsking(false)
                  onRemove(line)
                }}
              >
                Yes
              </Button>
              <Button size="small" onClick={() => setAsking(false)}>
                No
              </Button>
            </>
          ) : (
            <IconButton
              size="small"
              aria-label={`Remove ${line.item_name}`}
              title="Remove this item from the order"
              disabled={busy}
              onClick={() => setAsking(true)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

const OrderRow = ({
  request,
  lines,
  kind,
  busy,
  headHeight,
  onAct,
  onRemoveLine
}) => {
  const [open, setOpen] = React.useState(false)
  const [rowRef, rowHeight] = useHeight()
  const delivered = deliveredCount(lines)
  // Only an open order is frozen: a closed one is a single row with nothing
  // under it to scroll past.
  const pin = open ? frozen(headHeight, 2, '#fff') : undefined
  const itemHead = frozen(headHeight + rowHeight, 1, '#f1f1f1')
  return (
    <>
      <TableRow ref={rowRef}>
        <TableCell padding="none" style={pin}>
          <IconButton
            size="small"
            aria-label={open ? 'Hide items' : 'Show items'}
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={pin}>{request.po_number || '—'}</TableCell>
        <TableCell style={pin}>
          {request.apartment_name}
          <Typography variant="caption" color="textSecondary" display="block">
            {request.title || request.kit_name || ''}
          </Typography>
        </TableCell>
        <TableCell style={pin}>{request.requested_by_name || '—'}</TableCell>
        <TableCell style={pin}>
          {shortDate(KIND[kind].dateOf(request, lines))}
        </TableCell>
        <TableCell align="right" style={pin}>
          {lines.length}
        </TableCell>
        {kind === 'hand_over' && (
          <TableCell align="right" style={pin}>
            {delivered} of {lines.length}
          </TableCell>
        )}
        <TableCell align="right" style={pin}>
          {money(orderTotal(lines))}
        </TableCell>
        <TableCell align="right" style={{ whiteSpace: 'nowrap', ...pin }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            disabled={busy}
            onClick={() => onAct(request)}
          >
            {KIND[kind].action}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={kind === 'hand_over' ? 9 : 8}
          style={{ paddingTop: 0, paddingBottom: 0, borderBottom: 0 }}
        >
          <Collapse in={open} unmountOnExit>
            <Box mb={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={itemHead}>#</TableCell>
                    <TableCell style={itemHead}>Item</TableCell>
                    <TableCell align="right" style={itemHead}>
                      Qty
                    </TableCell>
                    <TableCell style={itemHead}>Vendor</TableCell>
                    {kind === 'hand_over' && (
                      <TableCell style={itemHead}>Status</TableCell>
                    )}
                    <TableCell align="right" style={itemHead}>
                      Amount
                    </TableCell>
                    {onRemoveLine && <TableCell style={itemHead} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, i) => (
                    <ItemRow
                      key={l.uuid}
                      line={l}
                      index={i}
                      kind={kind}
                      busy={busy}
                      onRemove={onRemoveLine}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const PendingOrdersDialog = ({
  kind,
  requests,
  liveLines,
  busy,
  onAct,
  onRemoveLine,
  onClose
}) => {
  // Keep the last kind while the dialog fades out, so it does not go blank.
  const lastKind = React.useRef(kind)
  if (kind) lastKind.current = kind
  const [headRef, headHeight] = useHeight()
  const shown = kind || lastKind.current
  if (!shown) return null
  const meta = KIND[shown]
  return (
    <Dialog open={!!kind} onClose={onClose} maxWidth="lg" fullWidth>
      {/* The intro sits with the title, outside the scrolling part, and the
          scrolling part has no top padding — otherwise rows show through the
          gap above the frozen headings. */}
      <DialogTitle>
        {meta.title} · {requests.length}
        <Typography variant="body2" color="textSecondary">
          {meta.intro}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 0, overflowX: 'hidden' }}>
        {requests.length === 0 ? (
          <Typography variant="body2">Nothing waiting.</Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow ref={headRef}>
                <TableCell padding="none" />
                <TableCell>PO</TableCell>
                <TableCell>Apartment · order</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>{meta.dateLabel}</TableCell>
                <TableCell align="right">Lines</TableCell>
                {shown === 'hand_over' && (
                  <TableCell align="right">Delivered</TableCell>
                )}
                <TableCell align="right">Amount</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <OrderRow
                  key={r.uuid}
                  request={r}
                  lines={liveLines(r)}
                  kind={shown}
                  busy={busy}
                  headHeight={headHeight}
                  onAct={onAct}
                  onRemoveLine={onRemoveLine}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PendingOrdersDialog
